import React from 'react';
import { tr } from '../../i18n';
import './PlayerAvatar.css';

type PlayerAvatarProps = {
  uid: string;
  displayName?: string;
  avatarUrl?: string | null;
  size?: number;
  glowRing?: boolean;
  online?: boolean;
};

/** Deterministic hue from uid string */
const uidToHue = (uid: string): number => {
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = (hash * 31 + uid.charCodeAt(i)) >>> 0;
  }
  return hash % 360;
};

const getInitials = (displayName?: string): string => {
  if (!displayName) return '?';
  const cleaned = displayName.replace(/^npc_dota_hero_/, '').replace(/_/g, ' ').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const PlayerAvatar: React.FC<PlayerAvatarProps> = ({
  uid,
  displayName,
  avatarUrl,
  size = 48,
  glowRing = false,
  online = false
}) => {
  const hue = uidToHue(uid);
  const bg = `hsl(${hue}, 40%, 28%)`;
  const border = `hsl(${hue}, 55%, 50%)`;
  const initials = getInitials(displayName);

  return (
    <div
      className={`player-avatar-wrap${glowRing ? ' player-avatar-wrap--glow' : ''}`}
      style={{
        width: size,
        height: size,
        '--avatar-hue': String(hue),
        '--avatar-glow-color': `hsl(${hue}, 55%, 50%)`
      } as React.CSSProperties}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={displayName || uid}
          className="player-avatar-img"
          style={{ width: size, height: size, borderColor: border }}
        />
      ) : (
        <div
          className="player-avatar-initials"
          style={{ width: size, height: size, background: bg, fontSize: size * 0.38 }}
          title={displayName || uid}
        >
          {initials}
        </div>
      )}
      {online && (
        <span
          className="player-avatar-online-dot"
          aria-label={tr.shell.avatar.online}
          style={{ width: size * 0.22, height: size * 0.22 }}
        />
      )}
    </div>
  );
};

export default PlayerAvatar;
